/**
 * Risk-Opportunity Matrix v2 — scatter plot + quadrant analysis
 * Improved: shows score components, entry quality, and more API fields
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, Skeleton, alpha, LinearProgress, Collapse,
  TextField, Chip, CircularProgress, Select, MenuItem,
} from '@mui/material';
import {
  Refresh, Assessment, OpenInNew, Shield, TrendingUp, TrendingDown,
  Warning, ArrowUpward, ArrowDownward, CheckCircle, ThumbUp,
  Search, Block, HourglassEmpty, Remove,
  KeyboardArrowDown, KeyboardArrowUp, GpsFixed,
  SwapHoriz, Timeline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { riskOpportunityAPI } from '../services/api';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea, Label,
  LineChart, Line, Legend,
} from 'recharts';

// ── Quadrant config ──
const Q_CFG = {
  A: { color: '#22c55e', label: '', desc: '' },
  B: { color: '#f97316', label: '', desc: '' },
  C: { color: '#3b82f6', label: '', desc: '' },
  D: { color: '#ef4444', label: '', desc: '' },
};

// ── Helpers ──
const fmtPct = (v, sign = true) => v == null ? '–' : `${sign && v > 0 ? '+' : ''}${v.toFixed(1)}%`;
const fmtPrice = (v) => v == null ? '–' : v >= 1000 ? v.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : v.toFixed(1);
const scoreClr = v => v >= 70 ? '#10b981' : v >= 50 ? '#3b82f6' : v >= 30 ? '#f59e0b' : '#ef4444';

// Quadrant upgrade/downgrade check
const _isUpgrade = (from, to) => {
  const rank = { A: 1, B: 2, C: 3, D: 4 };
  return (rank[to] || 5) < (rank[from] || 0);
};

// T+ Settlement: compute earliest sell date (T+2.5 in Vietnam = can sell morning T+3)
const getTplusDate = () => {
  const now = new Date();
  let days = 0;
  let d = new Date(now);
  while (days < 3) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) days++; // skip weekends
  }
  return d;
};
const fmtDate = (d) => `${d.getDate()}/${d.getMonth() + 1}`;

/**
 * Calculate trading plan from stock data
 * Entry: near MA20 (±1.5%)
 * StopLoss: max(support_20d, MA20 - 1.5*ATR) or support - 0.5*ATR
 * TakeProfit: entry + 2*(entry - SL) for R:R 2:1
 * Position: based on risk_adj score
 */
const getTradingPlan = (s) => {
  if (!s.current_price || !s.ma20_price) return null;
  const price = s.current_price;
  const ma20 = s.ma20_price;
  const atr = s.atr_14;
  const support = s.support_20d;
  const resistance = s.resistance_20d;

  // Entry zone: MA20 -1% to MA20 +2%
  const entryLow = ma20 * 0.99;
  const entryHigh = ma20 * 1.02;
  const inEntryZone = price >= entryLow * 0.98 && price <= entryHigh * 1.02;

  // Stop Loss
  let sl = null;
  if (atr && support) {
    sl = Math.max(support - 0.5 * atr, ma20 - 1.5 * atr);
  } else if (support) {
    sl = support * 0.98;
  } else if (atr) {
    sl = ma20 - 1.5 * atr;
  }

  // Ensure SL is below current price
  if (sl && sl >= price) sl = price * 0.95;

  // Take Profit (R:R = 2:1)
  let tp = null;
  let rr = null;
  if (sl) {
    const riskPerShare = price - sl;
    tp = price + 2 * riskPerShare;
    // Cap TP at resistance if close
    if (resistance && tp > resistance * 1.05) tp = resistance;
    rr = riskPerShare > 0 ? ((tp - price) / riskPerShare) : null;
  }

  // SL %
  const slPct = sl ? ((sl - price) / price * 100) : null;
  const tpPct = tp ? ((tp - price) / price * 100) : null;

  // Position size suggestion (% of portfolio)
  // Lower risk → larger position
  let posSize = 10; // default 10%
  const riskAdj = s.risk ?? 50;
  if (riskAdj < 30) posSize = 20;
  else if (riskAdj < 40) posSize = 15;
  else if (riskAdj < 50) posSize = 12;
  else if (riskAdj < 60) posSize = 8;
  else posSize = 5;

  return { price, ma20, atr, support, resistance, entryLow, entryHigh, inEntryZone, sl, tp, rr, slPct, tpPct, posSize };
};

// ── Action Signal Logic ──
const getActionSignal = (s, t) => {
  if (!s) return { label: '–', color: '#94a3b8', bg: '#f8fafc', sort: 99 };
  const ma20 = s.price_vs_ma20;
  const rsi = s.rsi_14;
  const ext = s.is_overextended;
  const ob = s.is_overbought;
  const g5 = s.gain_5d;
  const trend = s.o1_trend;  // Trend+RS score (0-100)

  // Red flags → AVOID
  if (ext || (rsi != null && rsi > 75) || (g5 != null && g5 > 15))
    return { label: t('riskOpportunityMatrix.action.avoid', 'Avoid'), color: '#ef4444', bg: 'rgba(239,68,68,0.08)', icon: <Block sx={{ fontSize: 12, color: '#ef4444' }} />, sort: 6 };

  // Weak trend → CONFIRM
  if (trend != null && trend < 40)
    return { label: t('riskOpportunityMatrix.action.confirm', 'Confirm'), color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: <Search sx={{ fontSize: 12, color: '#f59e0b' }} />, sort: 4 };

  // Overbought → CAUTION
  if (ob || (rsi != null && rsi > 65) || (ma20 != null && ma20 > 8))
    return { label: t('riskOpportunityMatrix.action.caution', 'Caution'), color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: <Warning sx={{ fontSize: 12, color: '#f59e0b' }} />, sort: 5 };

  // Ideal entry zone (trend >= 50, near MA20, low RSI)
  if ((trend == null || trend >= 50) && ma20 != null && ma20 >= -1 && ma20 <= 3 && (rsi == null || rsi < 60))
    return { label: t('riskOpportunityMatrix.action.buy', 'Buy'), color: '#10b981', bg: 'rgba(16,185,129,0.08)', icon: <CheckCircle sx={{ fontSize: 12, color: '#10b981' }} />, sort: 1 };

  // Good entry zone (trend >= 40)
  if ((trend == null || trend >= 40) && ma20 != null && ma20 >= -2 && ma20 <= 5 && (rsi == null || rsi < 70))
    return { label: t('riskOpportunityMatrix.action.good', 'Good'), color: '#3b82f6', bg: 'rgba(59,130,246,0.08)', icon: <ThumbUp sx={{ fontSize: 12, color: '#3b82f6' }} />, sort: 2 };

  // Pullback opportunity
  if (ma20 != null && ma20 < -1)
    return { label: t('riskOpportunityMatrix.action.wait', 'Wait'), color: '#8b5cf6', bg: 'rgba(139,92,246,0.08)', icon: <HourglassEmpty sx={{ fontSize: 12, color: '#8b5cf6' }} />, sort: 3 };

  return { label: t('riskOpportunityMatrix.action.neutral', 'Neutral'), color: '#64748b', bg: 'rgba(100,116,139,0.08)', icon: <Remove sx={{ fontSize: 12, color: '#64748b' }} />, sort: 3 };
};

// ── Compact score bar ──
const ScoreBar = ({ value, max = 100, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 56 }}>
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: color || scoreClr(value), minWidth: 20, textAlign: 'right' }}>{value?.toFixed(0) ?? '–'}</Typography>
    <LinearProgress variant="determinate" value={Math.min((value || 0) / max * 100, 100)}
      sx={{ flex: 1, height: 3, borderRadius: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: color || scoreClr(value), borderRadius: 1 } }} />
  </Box>
);

// ── Empty state ──
const EmptyState = ({ msg }) => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Box sx={{ width: 48, height: 48, borderRadius: '50%', border: '2px dashed #e2e8f0', mx: 'auto', mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Assessment sx={{ fontSize: 20, color: '#cbd5e1' }} />
    </Box>
    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8' }}>{msg}</Typography>
  </Box>
);

// ── Skeleton ──
const SkeletonScoreBar = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 56 }}>
    <Skeleton width={20} height={12} />
    <Skeleton variant="rounded" height={3} sx={{ flex: 1, borderRadius: 1 }} />
  </Box>
);

const PageSkeleton = () => (
  <Box sx={{ p: 2.5, maxWidth: 1400, mx: 'auto' }}>
    {/* Header */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Skeleton variant="rounded" width={42} height={42} sx={{ borderRadius: 2.5, bgcolor: alpha('#667eea', 0.12) }} animation="wave" />
        <Box>
          <Skeleton width={220} height={24} animation="wave" />
          <Skeleton width={160} height={14} sx={{ mt: 0.3 }} animation="wave" />
        </Box>
      </Box>
      <Skeleton variant="circular" width={36} height={36} animation="wave" />
    </Box>

    {/* Market Risk Info Strip */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <Skeleton variant="rounded" width={130} height={26} sx={{ borderRadius: 1.5 }} animation="wave" />
      <Box sx={{ color: '#e2e8f0', fontSize: '0.7rem' }}>|</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <Skeleton width={42} height={13} animation="wave" />
        <Skeleton variant="rounded" width={65} height={22} sx={{ borderRadius: 1 }} animation="wave" />
      </Box>
      <Box sx={{ color: '#e2e8f0', fontSize: '0.7rem' }}>|</Box>
      <Skeleton width={72} height={13} animation="wave" />
    </Box>

    {/* Tabs */}
    <Box sx={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', mb: 2 }}>
      <Box sx={{ px: 2.5, py: 1, borderBottom: '2px solid #667eea', mb: '-2px' }}>
        <Skeleton width={50} height={15} animation="wave" />
      </Box>
      <Box sx={{ px: 2.5, py: 1 }}>
        <Skeleton width={70} height={15} animation="wave" />
      </Box>
    </Box>

    {/* Matrix View: Chart + Table side by side */}
    <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', xl: 'row' } }}>
      {/* Scatter Chart Skeleton */}
      <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden', flex: { xs: '1', xl: '0 0 480px' } }}>
        {/* Chart header */}
        <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Skeleton width={160} height={16} animation="wave" />
          <Box sx={{ display: 'flex', gap: 0.4 }}>
            {['#22c55e','#f97316','#3b82f6','#ef4444'].map((c, i) => (
              <Skeleton key={i} variant="rounded" width={28} height={28} sx={{ borderRadius: 1, bgcolor: alpha(c, 0.12) }} animation="wave" />
            ))}
          </Box>
        </Box>

        {/* Chart area with quadrant zones */}
        <Box sx={{ p: 2, height: 380, position: 'relative' }}>
          {/* Quadrant background zones */}
          <Box sx={{ position: 'absolute', top: 16, left: 40, right: 16, bottom: 50, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' }}>
            <Box sx={{ bgcolor: alpha('#22c55e', 0.06), borderRight: '1.5px dashed #d1d5db', borderBottom: '1.5px dashed #d1d5db' }} />
            <Box sx={{ bgcolor: alpha('#f97316', 0.05), borderBottom: '1.5px dashed #d1d5db' }} />
            <Box sx={{ bgcolor: alpha('#3b82f6', 0.04), borderRight: '1.5px dashed #d1d5db' }} />
            <Box sx={{ bgcolor: alpha('#ef4444', 0.05) }} />
          </Box>
          {/* Fake scatter dots */}
          {[
            { x: '18%', y: '22%', c: '#22c55e' }, { x: '28%', y: '30%', c: '#22c55e' }, { x: '35%', y: '18%', c: '#22c55e' },
            { x: '22%', y: '35%', c: '#22c55e' }, { x: '30%', y: '28%', c: '#22c55e' },
            { x: '62%', y: '25%', c: '#f97316' }, { x: '72%', y: '20%', c: '#f97316' }, { x: '68%', y: '32%', c: '#f97316' },
            { x: '20%', y: '62%', c: '#3b82f6' }, { x: '30%', y: '68%', c: '#3b82f6' }, { x: '25%', y: '75%', c: '#3b82f6' },
            { x: '65%', y: '65%', c: '#ef4444' }, { x: '75%', y: '72%', c: '#ef4444' },
          ].map((d, i) => (
            <Box key={i} sx={{
              position: 'absolute', left: d.x, top: d.y,
              width: 24, height: 24, borderRadius: '50%',
              bgcolor: alpha(d.c, 0.18), border: `1.5px solid ${alpha(d.c, 0.25)}`,
              animation: 'pulse 1.5s ease-in-out infinite',
              animationDelay: `${i * 0.1}s`,
              '@keyframes pulse': { '0%, 100%': { opacity: 0.4 }, '50%': { opacity: 0.7 } },
            }} />
          ))}
          {/* Axis labels */}
          <Box sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)' }}>
            <Skeleton width={60} height={12} animation="wave" />
          </Box>
          <Box sx={{ position: 'absolute', top: '50%', left: 4, transform: 'translateY(-50%) rotate(-90deg)' }}>
            <Skeleton width={80} height={12} animation="wave" />
          </Box>
        </Box>

        {/* Legend bar */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, py: 0.8, borderTop: '1px solid #f1f5f9' }}>
          {['#22c55e','#f97316','#3b82f6','#ef4444'].map((c, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: alpha(c, 0.4) }} />
              <Skeleton width={[85, 90, 80, 35][i]} height={11} animation="wave" />
              <Skeleton width={30} height={10} animation="wave" />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Quadrant Detail Table Skeleton */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
          {/* Table header bar */}
          <Box sx={{ px: 2, py: 1.2, bgcolor: alpha('#22c55e', 0.04), borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1.5, bgcolor: alpha('#22c55e', 0.15) }} animation="wave" />
              <Box>
                <Skeleton width={130} height={15} animation="wave" />
                <Skeleton width={90} height={11} sx={{ mt: 0.2 }} animation="wave" />
              </Box>
            </Box>
            <Skeleton variant="rounded" width={65} height={22} sx={{ borderRadius: 1, bgcolor: alpha('#22c55e', 0.08) }} animation="wave" />
          </Box>

          {/* Table */}
          <Box>
            {/* Table header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: 0.8, py: 0.6, borderBottom: '1px solid #f1f5f9' }}>
              {[70, 90, 60, 80, 80, 60, 50, 40, 30].map((w, i) => (
                <Box key={i} sx={{ width: w, px: 0.8, flexShrink: 0 }}>
                  <Skeleton width={w * 0.7} height={11} animation="wave" />
                </Box>
              ))}
            </Box>
            {/* Table data rows */}
            {Array.from({ length: 10 }).map((_, ri) => (
              <Box key={ri} sx={{ display: 'flex', alignItems: 'center', px: 0.8, py: 0.5, borderBottom: '1px solid #f8fafc' }}>
                {/* Ticker */}
                <Box sx={{ width: 70, px: 0.8, flexShrink: 0 }}>
                  <Skeleton width={42} height={14} animation="wave" />
                </Box>
                {/* Sector */}
                <Box sx={{ width: 90, px: 0.8, flexShrink: 0 }}>
                  <Skeleton width={60} height={11} animation="wave" />
                </Box>
                {/* Priority */}
                <Box sx={{ width: 60, px: 0.8, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                  <Skeleton variant="rounded" width={30} height={16} sx={{ borderRadius: 0.8, bgcolor: alpha('#22c55e', 0.06) }} animation="wave" />
                </Box>
                {/* Trend+RS score bar */}
                <Box sx={{ width: 80, px: 0.8, flexShrink: 0 }}>
                  <SkeletonScoreBar />
                </Box>
                {/* Entry Setup score bar */}
                <Box sx={{ width: 80, px: 0.8, flexShrink: 0 }}>
                  <SkeletonScoreBar />
                </Box>
                {/* % MA20 */}
                <Box sx={{ width: 60, px: 0.8, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                  <Skeleton width={36} height={12} animation="wave" />
                </Box>
                {/* 5d return */}
                <Box sx={{ width: 50, px: 0.8, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                  <Skeleton width={32} height={12} animation="wave" />
                </Box>
                {/* RSI */}
                <Box sx={{ width: 40, px: 0.8, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                  <Skeleton width={22} height={12} animation="wave" />
                </Box>
                {/* Link icon */}
                <Box sx={{ width: 30, px: 0.4, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                  <Skeleton variant="circular" width={16} height={16} animation="wave" />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

// ══════════════════════════════════════
const RiskOpportunityMatrix = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [marketRisk, setMarketRisk] = useState(null);
  const [safeOpportunities, setSafeOpportunities] = useState([]);
  const [quadrantData, setQuadrantData] = useState({ A: [], B: [], C: [], D: [] });
  const [tab, setTab] = useState(0);
  const [selQ, setSelQ] = useState('A');
  const [hoveredTicker, setHoveredTicker] = useState(null);
  const [sortCol, setSortCol] = useState('priority');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedTicker, setExpandedTicker] = useState(null);
  const [signalAccuracy, setSignalAccuracy] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [minLiquidity, setMinLiquidity] = useState(10e9); // 10B VND default
  const [scoreHistory, setScoreHistory] = useState(null);
  const [scoreHistoryLoading, setScoreHistoryLoading] = useState(false);
  const [quadrantChanges, setQuadrantChanges] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [, setAvailableDates] = useState([]);

  Q_CFG.A.label = t('riskOpportunityMatrix.quadrants.A.label', 'Low Risk · High Opp');
  Q_CFG.A.desc = t('riskOpportunityMatrix.quadrants.A.desc', 'Best candidates');
  Q_CFG.B.label = t('riskOpportunityMatrix.quadrants.B.label', 'High Risk · High Opp');
  Q_CFG.B.desc = t('riskOpportunityMatrix.quadrants.B.desc', 'Need caution');
  Q_CFG.C.label = t('riskOpportunityMatrix.quadrants.C.label', 'Low Risk · Low Opp');
  Q_CFG.C.desc = t('riskOpportunityMatrix.quadrants.C.desc', 'Defensive plays');
  Q_CFG.D.label = t('riskOpportunityMatrix.quadrants.D.label', 'High Risk · Low Opp');
  Q_CFG.D.desc = t('riskOpportunityMatrix.quadrants.D.desc', 'Avoid');

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const fetchData = async (liq) => {
    const liqVal = liq != null ? liq : minLiquidity;
    setLoading(true); setError(null);
    try {
      const [mRes, sRes, ...qRes] = await Promise.all([
        riskOpportunityAPI.getMarketRisk(),
        riskOpportunityAPI.getSafeOpportunities(30, null, liqVal),
        ...['A','B','C','D'].map(q => riskOpportunityAPI.getStocksByQuadrant(q, 50, null, liqVal)),
      ]);
      setMarketRisk(mRes.data);
      // Default date picker to latest data date
      if (mRes.data?.date && !selectedDate) setSelectedDate(mRes.data.date);
      setSafeOpportunities(sRes.data);
      const qd = {};
      ['A','B','C','D'].forEach((q, i) => { qd[q] = qRes[i].data; });
      setQuadrantData(qd);
      // Background fetches (non-blocking)
      riskOpportunityAPI.getSignalAccuracy(90).then(r => setSignalAccuracy(r.data)).catch(() => {});
      riskOpportunityAPI.getQuadrantChanges(null, 1).then(r => setQuadrantChanges(r.data)).catch(() => {});
      riskOpportunityAPI.getAvailableDates().then(r => setAvailableDates(r.data?.dates || [])).catch(() => {});
    } catch (err) { setError(err.response?.data?.detail || err.message); }
    finally { setLoading(false); }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    if (selectedDate && selectedDate !== marketRisk?.date) {
      await fetchDataForDate(selectedDate);
    } else {
      await fetchData();
    }
    setRefreshing(false);
  };

  // Load score history when expanding a stock row
  const loadScoreHistory = async (ticker) => {
    if (expandedTicker === ticker) {
      setExpandedTicker(null);
      setScoreHistory(null);
      return;
    }
    setExpandedTicker(ticker);
    setScoreHistoryLoading(true);
    try {
      const res = await riskOpportunityAPI.getStockScoreHistory(ticker, 60);
      setScoreHistory(res.data);
    } catch {
      setScoreHistory(null);
    } finally {
      setScoreHistoryLoading(false);
    }
  };

  // Handle date change from picker
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    if (newDate) {
      // Reload data for the selected date
      fetchDataForDate(newDate);
    } else {
      fetchData(); // latest
    }
  };

  const fetchDataForDate = async (dateStr, liq) => {
    const liqVal = liq != null ? liq : minLiquidity;
    setLoading(true); setError(null);
    try {
      const [sRes, ...qRes] = await Promise.all([
        riskOpportunityAPI.getSafeOpportunities(30, dateStr, liqVal),
        ...['A','B','C','D'].map(q => riskOpportunityAPI.getStocksByQuadrant(q, 50, dateStr, liqVal)),
      ]);
      // Try to get market risk for that date
      try {
        const histRes = await riskOpportunityAPI.getMarketRiskHistory(365);
        const dayData = histRes.data?.history?.find(h => h.date === dateStr);
        if (dayData) {
          setMarketRisk({ date: dayData.date, market_risk: dayData.market_risk, regime: dayData.regime });
        }
      } catch {}
      setSafeOpportunities(sRes.data);
      const qd = {};
      ['A','B','C','D'].forEach((q, i) => { qd[q] = qRes[i].data; });
      setQuadrantData(qd);
      riskOpportunityAPI.getQuadrantChanges(dateStr, 1).then(r => setQuadrantChanges(r.data)).catch(() => {});
    } catch (err) { setError(err.response?.data?.detail || err.message); }
    finally { setLoading(false); }
  };

  const riskClr = v => v < 40 ? '#10b981' : v < 60 ? '#f59e0b' : '#ef4444';
  const riskLbl = v => v < 40 ? t('riskOpportunityMatrix.market.low', 'Low') : v < 60 ? t('riskOpportunityMatrix.market.moderate', 'Moderate') : t('riskOpportunityMatrix.market.high', 'High');
  const regimeClr = r => r === 'RISK_ON' ? '#10b981' : r === 'RISK_OFF' ? '#ef4444' : r === 'SIDEWAY' ? '#f59e0b' : '#3b82f6';
  const tabs = [t('riskOpportunityMatrix.matrixView', 'Matrix'), t('riskOpportunityMatrix.safeOpportunities', 'Safe Entry')];

  const handleSort = (col) => {
    if (sortCol === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortCol(col); setSortDir('desc'); }
  };

  const getSorted = (data) => {
    if (!data || data.length === 0) return [];
    const sorted = [...data].sort((a, b) => {
      // Always sort by action quality first, then by selected column
      const aAction = getActionSignal(a, t).sort;
      const bAction = getActionSignal(b, t).sort;
      if (aAction !== bAction) return aAction - bAction;
      const av = a[sortCol] ?? -999;
      const bv = b[sortCol] ?? -999;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return sorted;
  };

  if (loading) return <PageSkeleton />;

  if (error) {
    return (
      <Box sx={{ p: 2.5, maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{ px: 2, py: 1, borderRadius: 1.5, bgcolor: alpha('#ef4444', 0.06), border: `1px solid ${alpha('#ef4444', 0.15)}` }}>
          <Typography sx={{ fontSize: '0.8rem', color: '#ef4444' }}>{error}</Typography>
        </Box>
      </Box>
    );
  }

  const totalStocks = Object.values(quadrantData).flat().length;

  return (
    <Box sx={{ p: 2.5, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2.5, flexShrink: 0,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 10px ${alpha('#667eea', 0.3)}`,
          }}>
            <Assessment sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
              {t('riskOpportunityMatrix.title', 'Risk-Opportunity Matrix')}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500, mt: 0.2 }}>
              {totalStocks} {t('riskOpportunityMatrix.stocks', 'stocks')} · {t('riskOpportunityMatrix.subtitle', '4-quadrant analysis')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Liquidity Filter */}
          <Select
            value={minLiquidity}
            onChange={(e) => {
              const v = e.target.value;
              setMinLiquidity(v);
              if (selectedDate) fetchDataForDate(selectedDate, v);
              else fetchData(v);
            }}
            size="small"
            sx={{
              height: 32, fontSize: '0.72rem', fontWeight: 600, borderRadius: 1.5,
              bgcolor: alpha('#667eea', 0.04),
              '& .MuiSelect-select': { py: 0.5, px: 1.2 },
              '& fieldset': { borderColor: alpha('#667eea', 0.15) },
              '&:hover fieldset': { borderColor: alpha('#667eea', 0.3) },
              minWidth: 100,
            }}
          >
            <MenuItem value={0} sx={{ fontSize: '0.8rem' }}>{t('riskOpportunityMatrix.liquidity.all', 'Tất cả')}</MenuItem>
            <MenuItem value={5e9} sx={{ fontSize: '0.8rem' }}>≥ 5B</MenuItem>
            <MenuItem value={10e9} sx={{ fontSize: '0.8rem' }}>≥ 10B</MenuItem>
            <MenuItem value={20e9} sx={{ fontSize: '0.8rem' }}>≥ 20B</MenuItem>
            <MenuItem value={50e9} sx={{ fontSize: '0.8rem' }}>≥ 50B</MenuItem>
          </Select>
          {/* Date Picker */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              type="date"
              size="small"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              InputProps={{
                sx: {
                  fontSize: '0.72rem', height: 32, borderRadius: 1.5,
                  '& input': { py: 0.5, px: 1, cursor: 'pointer' },
                  bgcolor: alpha('#667eea', 0.04),
                  border: `1px solid ${alpha('#667eea', 0.15)}`,
                  '&:hover': { borderColor: alpha('#667eea', 0.3) },
                },
              }}
              sx={{ width: 140 }}
            />
          </Box>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <Refresh sx={{ fontSize: 16 }} />}
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
            sx={{
              minWidth: 100, height: 32, fontWeight: 600, fontSize: '0.8rem',
              textTransform: 'none', borderRadius: 1.5, px: 1.5,
              color: '#667eea',
              borderColor: alpha('#667eea', 0.3),
              bgcolor: alpha('#667eea', 0.04),
              transition: 'all 0.2s ease',
              '&:hover': { borderColor: '#667eea', bgcolor: alpha('#667eea', 0.08) },
              '&:disabled': { opacity: 0.6 },
            }}
          >
            {refreshing ? t('common.refreshing', 'Đang tải...') : t('common.refresh', 'Làm mới')}
          </Button>
        </Box>
      </Box>

      {/* Market Risk Info Strip */}
      {marketRisk && (() => {
        const rc = riskClr(marketRisk.market_risk);
        const rgc = regimeClr(marketRisk.regime);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1, py: 0.35, borderRadius: 1.5, bgcolor: alpha(rc, 0.08), border: `1px solid ${alpha(rc, 0.12)}` }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: rc }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155' }}>
                {t('riskOpportunityMatrix.market.risk', 'Risk')}: {marketRisk.market_risk?.toFixed(0)}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: rc }}>{riskLbl(marketRisk.market_risk)}</Typography>
            </Box>
            <Typography sx={{ color: '#e2e8f0', fontSize: '0.7rem' }}>|</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>{t('riskOpportunityMatrix.market.regime', 'Regime')}:</Typography>
              <Box sx={{ px: 0.7, py: 0.15, borderRadius: 1, bgcolor: alpha(rgc, 0.1), border: `1px solid ${alpha(rgc, 0.15)}` }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: rgc }}>{marketRisk.regime}</Typography>
              </Box>
            </Box>
            <Typography sx={{ color: '#e2e8f0', fontSize: '0.7rem' }}>|</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{marketRisk.date}</Typography>
          </Box>
        );
      })()}

      {/* Quadrant Changes Today */}
      {quadrantChanges && quadrantChanges.total_changes > 0 && (
        <Box sx={{
          mb: 1.5, borderRadius: 1.5, border: '1px solid',
          borderColor: quadrantChanges.upgrades > quadrantChanges.downgrades ? alpha('#10b981', 0.2) : alpha('#ef4444', 0.2),
          bgcolor: quadrantChanges.upgrades > quadrantChanges.downgrades ? alpha('#10b981', 0.03) : alpha('#ef4444', 0.03),
          overflow: 'hidden',
        }}>
          <Box sx={{ px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <SwapHoriz sx={{ fontSize: 14, color: '#667eea' }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569' }}>
                {t('riskOpportunityMatrix.changes.title', 'Quadrant Changes')}
              </Typography>
            </Box>
            {quadrantChanges.upgrades > 0 && (
              <Chip
                size="small"
                icon={<ArrowUpward sx={{ fontSize: 10 }} />}
                label={`${quadrantChanges.upgrades} ${t('riskOpportunityMatrix.changes.upgrades', 'upgrades')}`}
                sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha('#10b981', 0.1), color: '#10b981', '& .MuiChip-icon': { color: '#10b981' } }}
              />
            )}
            {quadrantChanges.downgrades > 0 && (
              <Chip
                size="small"
                icon={<ArrowDownward sx={{ fontSize: 10 }} />}
                label={`${quadrantChanges.downgrades} ${t('riskOpportunityMatrix.changes.downgrades', 'downgrades')}`}
                sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha('#ef4444', 0.1), color: '#ef4444', '& .MuiChip-icon': { color: '#ef4444' } }}
              />
            )}
            <Typography sx={{ color: '#e2e8f0', fontSize: '0.6rem' }}>|</Typography>
            {/* Show individual changes as compact chips */}
            {quadrantChanges.changes.slice(0, 12).map(c => {
              const isUp = _isUpgrade(c.from, c.to);
              return (
                <Chip
                  key={c.ticker}
                  size="small"
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#1e293b' }}>{c.ticker}</Typography>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: Q_CFG[c.from]?.color }}>{c.from}</Typography>
                      <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8' }}>→</Typography>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: Q_CFG[c.to]?.color }}>{c.to}</Typography>
                    </Box>
                  }
                  sx={{
                    height: 20, bgcolor: isUp ? alpha('#10b981', 0.06) : alpha('#ef4444', 0.06),
                    border: `1px solid ${isUp ? alpha('#10b981', 0.15) : alpha('#ef4444', 0.15)}`,
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              );
            })}
            {quadrantChanges.changes.length > 12 && (
              <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600 }}>
                +{quadrantChanges.changes.length - 12} {t('riskOpportunityMatrix.changes.more', 'more')}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Tabs */}
      <Box sx={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', mb: 2 }}>
        {tabs.map((label, i) => (
          <Box key={i} onClick={() => setTab(i)}
            sx={{
              px: 2.5, py: 1, cursor: 'pointer', position: 'relative',
              borderBottom: tab === i ? '2px solid #667eea' : '2px solid transparent',
              mb: '-2px', transition: 'all 0.15s',
            }}>
            <Typography sx={{
              fontSize: '0.82rem', fontWeight: tab === i ? 700 : 500,
              color: tab === i ? '#667eea' : '#94a3b8',
            }}>
              {label}
              {i === 1 && safeOpportunities.length > 0 && (
                <Typography component="span" sx={{ ml: 0.5, fontSize: '0.68rem', fontWeight: 700, color: '#10b981', bgcolor: alpha('#10b981', 0.08), px: 0.5, py: 0.1, borderRadius: 0.5 }}>
                  {safeOpportunities.length}
                </Typography>
              )}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Tab 0: Matrix View */}
      {tab === 0 && (
        <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', xl: 'row' } }}>
          {/* Scatter Chart */}
          <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden', flex: { xs: '1', xl: '0 0 620px' } }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
                {t('riskOpportunityMatrix.title', 'Risk-Opportunity Matrix')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.4 }}>
                {['A','B','C','D'].map(q => (
                  <Box key={q} onClick={() => setSelQ(q)}
                    sx={{
                      width: 28, height: 28, borderRadius: 1, cursor: 'pointer',
                      bgcolor: selQ === q ? Q_CFG[q].color : alpha(Q_CFG[q].color, 0.08),
                      color: selQ === q ? '#fff' : Q_CFG[q].color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 800, fontSize: '0.72rem', transition: 'all 0.15s',
                      border: selQ === q ? 'none' : `1px solid ${alpha(Q_CFG[q].color, 0.15)}`,
                      '&:hover': { bgcolor: selQ === q ? Q_CFG[q].color : alpha(Q_CFG[q].color, 0.15) },
                    }}>
                    {quadrantData[q]?.length || 0}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ p: 2, height: 520 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 20 }}>
                  {(() => {
                    const highOpp = [...(quadrantData.A || []), ...(quadrantData.B || [])];
                    const lowRisk = [...(quadrantData.A || []), ...(quadrantData.C || [])];
                    const oppTh = highOpp.length > 0 ? Math.min(...highOpp.map(s => s.opportunity || 0)) - 1 : 55;
                    const riskTh = lowRisk.length > 0 ? Math.max(...lowRisk.map(s => s.risk || 0)) + 1 : 45;
                    return (
                      <>
                        <ReferenceArea x1={0} x2={riskTh} y1={oppTh} y2={100} fill="#22c55e" fillOpacity={0.12} />
                        <ReferenceArea x1={riskTh} x2={100} y1={oppTh} y2={100} fill="#f97316" fillOpacity={0.1} />
                        <ReferenceArea x1={0} x2={riskTh} y1={0} y2={oppTh} fill="#3b82f6" fillOpacity={0.06} />
                        <ReferenceArea x1={riskTh} x2={100} y1={0} y2={oppTh} fill="#ef4444" fillOpacity={0.1} />
                        <ReferenceLine x={riskTh} stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="6 4" />
                        <ReferenceLine y={oppTh} stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="6 4" />
                      </>
                    );
                  })()}
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" dataKey="risk" domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} ticks={[0, 20, 40, 60, 80, 100]}>
                    <Label value="RISK →" position="bottom" offset={20} style={{ fontSize: 11, fontWeight: 700, fill: '#94a3b8' }} />
                  </XAxis>
                  <YAxis type="number" dataKey="opportunity" domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} ticks={[0, 20, 40, 60, 80, 100]}>
                    <Label value="OPPORTUNITY" angle={-90} position="left" offset={0} style={{ fontSize: 11, fontWeight: 700, fill: '#94a3b8' }} />
                  </YAxis>
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} wrapperStyle={{ zIndex: 1000 }} content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <Box sx={{ bgcolor: '#fff', p: 1.2, borderRadius: 1.5, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0', minWidth: 170, position: 'relative', zIndex: 1000 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: Q_CFG[d.quadrant]?.color }}>{d.ticker}</Typography>
                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#fff', bgcolor: Q_CFG[d.quadrant]?.color, px: 0.5, py: 0.1, borderRadius: 0.5 }}>{d.quadrant}</Typography>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px' }}>
                          {[
                            ['Sector', d.sector_name?.[i18n.language] || d.sector_code?.replace(/_/g, ' ')],
                            ['Opportunity', d.opportunity?.toFixed(1)],
                            ['Risk', d.risk?.toFixed(1)],
                            ['Priority', d.priority?.toFixed(1)],
                          ].map(([k, v]) => (
                            <React.Fragment key={k}>
                              <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>{k}</Typography>
                              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#334155' }}>{v}</Typography>
                            </React.Fragment>
                          ))}
                        </Box>
                        <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid #f1f5f9', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px' }}>
                          {[
                            ['Trend+RS', d.o1_trend?.toFixed(0)],
                            ['Entry Setup', d.o2_flow?.toFixed(0)],
                            ['% MA20', fmtPct(d.price_vs_ma20)],
                            ['5d Return', fmtPct(d.gain_5d)],
                            ['RSI', d.rsi_14?.toFixed(0)],
                          ].map(([k, v]) => (
                            <React.Fragment key={k}>
                              <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>{k}</Typography>
                              <Typography sx={{
                                fontSize: '0.58rem', fontWeight: 700,
                                color: k === '5d Return' ? ((d.gain_5d || 0) >= 0 ? '#10b981' : '#ef4444')
                                     : k === '% MA20' ? ((d.price_vs_ma20 || 0) >= 0 ? '#10b981' : '#ef4444')
                                     : '#475569'
                              }}>{v}</Typography>
                            </React.Fragment>
                          ))}
                        </Box>
                      </Box>
                    );
                  }} />
                  {['A','B','C','D'].map(q => {
                    const items = (quadrantData[q] || []).map(s => ({ ...s, quadrant: q }));
                    const isSel = selQ === q;
                    if (isSel) items.sort((a, b) => (b.priority || 0) - (a.priority || 0));
                    return (
                      <Scatter key={q} name={Q_CFG[q].label}
                        data={items}
                        fill={Q_CFG[q].color}
                        onClick={d => { setSelQ(d.quadrant); setHoveredTicker(null); }}
                        onMouseEnter={(entry) => setHoveredTicker(entry?.ticker)}
                        onMouseLeave={() => setHoveredTicker(null)}
                        cursor="pointer"
                        shape={({ cx, cy, payload, index }) => {
                          const isHov = hoveredTicker === payload.ticker;
                          const priority = payload.priority || 50;
                          const qColor = Q_CFG[q]?.color;
                          const baseR = isSel ? 5 + (priority / 100) * 5 : 3 + (priority / 100) * 2;
                          const r = isHov ? Math.max(baseR, 9) + 3 : baseR;
                          const fillOp = isHov ? 0.95 : isSel ? 0.75 : 0.18;
                          const showLabel = isHov || (isSel && index < 15);
                          const labelAbove = index % 2 === 0;
                          const labelY = labelAbove ? cy - r - 3 : cy + r + 3;
                          return (
                            <g>
                              {isHov && <circle cx={cx} cy={cy} r={r + 5} fill="none" stroke={qColor} strokeWidth={1.5} opacity={0.3} />}
                              <circle cx={cx} cy={cy} r={r}
                                fill={qColor} fillOpacity={fillOp}
                                stroke={isHov ? '#fff' : isSel ? 'rgba(255,255,255,0.5)' : 'none'}
                                strokeWidth={isHov ? 2 : isSel ? 0.8 : 0}
                              />
                              {showLabel && (
                                <text x={cx} y={labelY}
                                  textAnchor="middle"
                                  dominantBaseline={labelAbove ? 'auto' : 'hanging'}
                                  fill={isHov ? '#1e293b' : qColor}
                                  fontSize={isHov ? 11 : 8}
                                  fontWeight={isHov ? 800 : 600}
                                  style={{ pointerEvents: 'none' }}
                                >{payload.ticker}</text>
                              )}
                            </g>
                          );
                        }}
                      />
                    );
                  })}
                </ScatterChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, py: 0.8, borderTop: '1px solid #f1f5f9' }}>
              {['A','B','C','D'].map(q => {
                const stocks = quadrantData[q] || [];
                const avg = stocks.length > 0 ? stocks.reduce((s, x) => s + (x.gain_5d || 0), 0) / stocks.length : 0;
                return (
                  <Box key={q} onClick={() => setSelQ(q)} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.4, opacity: selQ === q ? 1 : 0.6, transition: 'all 0.15s', '&:hover': { opacity: 1 } }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: Q_CFG[q].color }} />
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: selQ === q ? 700 : 500, color: selQ === q ? Q_CFG[q].color : '#64748b' }}>
                      {Q_CFG[q].label} {stocks.length}
                    </Typography>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: avg >= 0 ? '#10b981' : '#ef4444' }}>
                      ({avg >= 0 ? '+' : ''}{avg.toFixed(1)}%)
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Quadrant Detail Table */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.2, bgcolor: alpha(Q_CFG[selQ].color, 0.04), borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: Q_CFG[selQ].color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.95rem' }}>
                    {selQ}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>{Q_CFG[selQ].label}</Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{Q_CFG[selQ].desc}</Typography>
                  </Box>
                </Box>
                <Box sx={{ px: 0.8, py: 0.2, borderRadius: 1, bgcolor: alpha(Q_CFG[selQ].color, 0.1) }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: Q_CFG[selQ].color }}>
                    {quadrantData[selQ]?.length || 0} {t('riskOpportunityMatrix.stocks', 'stocks')}
                  </Typography>
                </Box>
              </Box>

              {(quadrantData[selQ] || []).length === 0 ? (
                <EmptyState msg={t('riskOpportunityMatrix.noStocks', 'No stocks in this quadrant')} />
              ) : (() => {
                const sorted = getSorted(quadrantData[selQ] || []);
                const actionCounts = {};
                sorted.forEach(s => { const a = getActionSignal(s, t); actionCounts[a.label] = (actionCounts[a.label] || { count: 0, color: a.color, icon: a.icon, sort: a.sort }); actionCounts[a.label].count++; });
                const chips = Object.entries(actionCounts).sort((a, b) => a[1].sort - b[1].sort);
                // Find index where non-actionable starts (sort >= 4)
                return (
                <>
                {/* Signal Accuracy Bar */}
                {signalAccuracy?.accuracy && (() => {
                  const qacc = signalAccuracy.accuracy.filter(a => a.quadrant === selQ);
                  const high = qacc.find(a => a.priority_bucket === 'high');
                  const med = qacc.find(a => a.priority_bucket === 'medium');
                  const best = high || med;
                  if (!best) return null;
                  return (
                    <Box sx={{ px: 2, py: 0.5, display: 'flex', gap: 1.5, alignItems: 'center', borderBottom: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
                      <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600 }}>
                        {t('riskOpportunityMatrix.signalAccuracy.title', '90d Signal Stats')}:
                      </Typography>
                      {best.win_rate_5d != null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>5d</Typography>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: best.win_rate_5d >= 55 ? '#10b981' : best.win_rate_5d >= 45 ? '#f59e0b' : '#ef4444' }}>
                            {best.win_rate_5d.toFixed(0)}% win
                          </Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: (best.avg_ret_5d || 0) >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                            ({best.avg_ret_5d >= 0 ? '+' : ''}{best.avg_ret_5d?.toFixed(1)}%)
                          </Typography>
                        </Box>
                      )}
                      {best.win_rate_10d != null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>10d</Typography>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: best.win_rate_10d >= 55 ? '#10b981' : best.win_rate_10d >= 45 ? '#f59e0b' : '#ef4444' }}>
                            {best.win_rate_10d.toFixed(0)}% win
                          </Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: (best.avg_ret_10d || 0) >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                            ({best.avg_ret_10d >= 0 ? '+' : ''}{best.avg_ret_10d?.toFixed(1)}%)
                          </Typography>
                        </Box>
                      )}
                      {best.win_rate_20d != null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>20d</Typography>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: best.win_rate_20d >= 55 ? '#10b981' : best.win_rate_20d >= 45 ? '#f59e0b' : '#ef4444' }}>
                            {best.win_rate_20d.toFixed(0)}% win
                          </Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: (best.avg_ret_20d || 0) >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                            ({best.avg_ret_20d >= 0 ? '+' : ''}{best.avg_ret_20d?.toFixed(1)}%)
                          </Typography>
                        </Box>
                      )}
                      <Typography sx={{ fontSize: '0.52rem', color: '#cbd5e1', ml: 'auto' }}>
                        n={best.total_signals}
                      </Typography>
                    </Box>
                  );
                })()}
                {/* Action summary chips */}
                <Box sx={{ px: 2, py: 0.8, display: 'flex', gap: 0.8, flexWrap: 'wrap', borderBottom: '1px solid #f1f5f9' }}>
                  {chips.map(([label, { count, color, icon }]) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.8, py: 0.2, borderRadius: 1, bgcolor: alpha(color, 0.08) }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color }}>{count} {label}</Typography>
                    </Box>
                  ))}
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {[
                          { label: t('riskOpportunityMatrix.table.ticker', 'Mã CK'), align: 'left', w: 70, col: null },
                          { label: 'Action', align: 'center', w: 68, col: null },
                          { label: t('riskOpportunityMatrix.table.priority', 'Ưu tiên'), align: 'center', w: 52, col: 'priority' },
                          { label: 'Trend+RS', align: 'center', w: 68, col: 'o1_trend' },
                          { label: 'Entry', align: 'center', w: 68, col: 'o2_flow' },
                          { label: '% MA20', align: 'center', w: 52, col: 'price_vs_ma20' },
                          { label: 'Vol', align: 'center', w: 40, col: 'volume_surge_20d' },
                          { label: '5d', align: 'center', w: 40, col: 'gain_5d' },
                          { label: '20d', align: 'center', w: 40, col: 'gain_20d' },
                          { label: 'RSI', align: 'center', w: 36, col: 'rsi_14' },
                          { label: '', align: 'center', w: 24, col: null },
                        ].map((h, i) => (
                          <TableCell key={i} align={h.align}
                            onClick={() => h.col && handleSort(h.col)}
                            sx={{
                              fontWeight: 700, fontSize: '0.65rem', color: sortCol === h.col ? '#667eea' : '#64748b',
                              py: 0.6, px: 0.8, borderColor: '#f1f5f9', bgcolor: '#fff', width: h.w,
                              ...(i === 0 && { pl: 2 }),
                              cursor: h.col ? 'pointer' : 'default', userSelect: 'none',
                              '&:hover': h.col ? { color: '#667eea' } : {},
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: h.align === 'left' ? 'flex-start' : 'center', gap: 0.2 }}>
                              {h.label}
                              {sortCol === h.col && (
                                sortDir === 'desc'
                                  ? <ArrowDownward sx={{ fontSize: 11, color: '#667eea' }} />
                                  : <ArrowUpward sx={{ fontSize: 11, color: '#667eea' }} />
                              )}
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sorted.map((s, idx) => {
                        const action = getActionSignal(s, t);
                        const isTop = idx < 3 && action.sort <= 2;
                        const isDimmed = action.sort >= 4;
                        const vol = s.volume_surge_20d;
                        return (
                        <React.Fragment key={s.ticker}>
                        <TableRow hover sx={{
                          ...(isTop && { bgcolor: alpha(Q_CFG[selQ].color, 0.03), borderLeft: `2px solid ${alpha(Q_CFG[selQ].color, 0.5)}` }),
                          ...(isDimmed && { opacity: 0.45 }),
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(Q_CFG[selQ].color, 0.06), opacity: 1 },
                        }}
                        onClick={() => loadScoreHistory(s.ticker)}
                        >
                          <TableCell sx={{ py: 0.4, px: 0.8, pl: 2, borderColor: '#f8fafc' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              {isTop && <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: Q_CFG[selQ].color, minWidth: 10 }}>{idx + 1}</Typography>}
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>{s.ticker}</Typography>
                              {s.is_overextended && (
                                <Warning sx={{ fontSize: 12, color: '#ef4444' }} titleAccess="Overextended" />
                              )}
                              {s.is_overbought && !s.is_overextended && (
                                <Warning sx={{ fontSize: 12, color: '#f59e0b' }} titleAccess="Overbought" />
                              )}
                            </Box>
                            <Typography sx={{ fontSize: '0.56rem', color: '#94a3b8', lineHeight: 1, ml: isTop ? 1.4 : 0 }}>{s.sector_name?.[i18n.language] || s.sector_code?.replace(/_/g, ' ')}</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4, px: 0.4, borderColor: '#f8fafc' }}>
                            <Typography sx={{
                              fontSize: '0.6rem', fontWeight: 700, color: action.color,
                              bgcolor: action.bg, px: 0.5, py: 0.15, borderRadius: 0.5,
                              display: 'inline-block', lineHeight: 1.3,
                            }}>
                              {action.icon} {action.label}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4, px: 0.8, borderColor: '#f8fafc' }}>
                            <Typography sx={{ display: 'inline-block', px: 0.5, py: 0.1, borderRadius: 0.8, bgcolor: alpha(Q_CFG[selQ].color, 0.08), color: Q_CFG[selQ].color, fontWeight: 800, fontSize: '0.72rem' }}>
                              {s.priority?.toFixed(1) ?? '–'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.4, px: 0.8, borderColor: '#f8fafc' }}>
                            <ScoreBar value={s.o1_trend} />
                          </TableCell>
                          <TableCell sx={{ py: 0.4, px: 0.8, borderColor: '#f8fafc' }}>
                            <ScoreBar value={s.o2_flow} />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4, px: 0.8, borderColor: '#f8fafc' }}>
                            {s.price_vs_ma20 != null ? (
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: s.price_vs_ma20 >= 0 && s.price_vs_ma20 <= 5 ? '#10b981' : s.price_vs_ma20 > 8 ? '#ef4444' : '#f59e0b' }}>
                                {fmtPct(s.price_vs_ma20)}
                              </Typography>
                            ) : '–'}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4, px: 0.4, borderColor: '#f8fafc' }}>
                            {vol != null ? (
                              <Typography sx={{
                                fontSize: '0.66rem', fontWeight: 700,
                                color: vol >= 2.0 ? '#10b981' : vol >= 1.3 ? '#3b82f6' : vol < 0.5 ? '#ef4444' : '#64748b',
                              }}>
                                {vol.toFixed(1)}x
                              </Typography>
                            ) : <Typography sx={{ fontSize: '0.66rem', color: '#cbd5e1' }}>–</Typography>}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4, px: 0.6, borderColor: '#f8fafc' }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: (s.gain_5d || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                              {fmtPct(s.gain_5d)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4, px: 0.6, borderColor: '#f8fafc' }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: (s.gain_20d || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                              {fmtPct(s.gain_20d)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.4, px: 0.8, borderColor: '#f8fafc' }}>
                            <Typography sx={{ fontSize: '0.68rem', color: (s.rsi_14 || 50) > 70 ? '#ef4444' : (s.rsi_14 || 50) < 30 ? '#10b981' : '#475569' }}>
                              {s.rsi_14?.toFixed(0) ?? '–'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.4, px: 0.4, borderColor: '#f8fafc' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/stocks/${s.ticker}`); }} sx={{ width: 20, height: 20, color: '#94a3b8', '&:hover': { color: Q_CFG[selQ].color } }}>
                                <OpenInNew sx={{ fontSize: 12 }} />
                              </IconButton>
                              {expandedTicker === s.ticker
                                ? <KeyboardArrowUp sx={{ fontSize: 14, color: '#94a3b8' }} />
                                : <KeyboardArrowDown sx={{ fontSize: 14, color: '#94a3b8' }} />}
                            </Box>
                          </TableCell>
                        </TableRow>
                        {/* Expandable Trading Plan + Score History Row */}
                        <TableRow>
                          <TableCell colSpan={11} sx={{ py: 0, px: 0, borderColor: expandedTicker === s.ticker ? '#e2e8f0' : 'transparent', borderBottom: expandedTicker === s.ticker ? '1px solid #e2e8f0' : 'none' }}>
                            <Collapse in={expandedTicker === s.ticker} timeout="auto" unmountOnExit>
                              {(() => {
                                const plan = getTradingPlan(s);
                                return (
                                  <Box sx={{ bgcolor: '#f8fafc' }}>
                                    {/* Trading Plan */}
                                    {!plan ? (
                                      <Box sx={{ px: 2, py: 1 }}>
                                        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                          {t('riskOpportunityMatrix.tradingPlan.noData', 'No price data')}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Box sx={{ px: 2, py: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Box sx={{ minWidth: 65 }}>
                                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600, lineHeight: 1, mb: 0.2 }}>
                                            {t('riskOpportunityMatrix.tradingPlan.currentPrice', 'Current')}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>{fmtPrice(plan.price)}</Typography>
                                        </Box>
                                        <Box sx={{ minWidth: 90 }}>
                                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600, lineHeight: 1, mb: 0.2 }}>
                                            <GpsFixed sx={{ fontSize: 9, mr: 0.3, verticalAlign: 'middle' }} />
                                            {t('riskOpportunityMatrix.tradingPlan.entryZone', 'Entry Zone')}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: plan.inEntryZone ? '#10b981' : '#f59e0b' }}>
                                            {fmtPrice(plan.entryLow)} – {fmtPrice(plan.entryHigh)}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ minWidth: 70 }}>
                                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600, lineHeight: 1, mb: 0.2 }}>
                                            {t('riskOpportunityMatrix.tradingPlan.stopLoss', 'Stop Loss')}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#ef4444' }}>
                                            {fmtPrice(plan.sl)}
                                            {plan.slPct != null && <Typography component="span" sx={{ fontSize: '0.58rem', color: '#ef4444', ml: 0.3 }}>({plan.slPct.toFixed(1)}%)</Typography>}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ minWidth: 70 }}>
                                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600, lineHeight: 1, mb: 0.2 }}>
                                            {t('riskOpportunityMatrix.tradingPlan.takeProfit', 'Take Profit')}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#10b981' }}>
                                            {fmtPrice(plan.tp)}
                                            {plan.tpPct != null && <Typography component="span" sx={{ fontSize: '0.58rem', color: '#10b981', ml: 0.3 }}>(+{plan.tpPct.toFixed(1)}%)</Typography>}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ minWidth: 40 }}>
                                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600, lineHeight: 1, mb: 0.2 }}>
                                            {t('riskOpportunityMatrix.tradingPlan.riskReward', 'R:R')}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: plan.rr >= 2 ? '#10b981' : plan.rr >= 1.5 ? '#3b82f6' : '#f59e0b' }}>
                                            {plan.rr != null ? `1:${plan.rr.toFixed(1)}` : '–'}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ minWidth: 50 }}>
                                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600, lineHeight: 1, mb: 0.2 }}>
                                            {t('riskOpportunityMatrix.tradingPlan.positionSize', 'Position')}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: plan.posSize >= 15 ? '#10b981' : plan.posSize >= 10 ? '#3b82f6' : '#f59e0b', bgcolor: plan.posSize >= 15 ? 'rgba(16,185,129,0.08)' : plan.posSize >= 10 ? 'rgba(59,130,246,0.08)' : 'rgba(245,158,11,0.08)', px: 0.5, py: 0.1, borderRadius: 0.5, display: 'inline-block' }}>
                                            {plan.posSize}%
                                          </Typography>
                                        </Box>
                                        <Box sx={{ minWidth: 80, borderLeft: '1px solid #e2e8f0', pl: 1.5 }}>
                                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600, lineHeight: 1, mb: 0.2 }}>
                                            {t('riskOpportunityMatrix.tradingPlan.support', 'Support')} / {t('riskOpportunityMatrix.tradingPlan.resistance', 'Resistance')}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.66rem', color: '#475569' }}>
                                            <Typography component="span" sx={{ fontSize: '0.66rem', color: '#ef4444', fontWeight: 600 }}>{fmtPrice(plan.support)}</Typography>
                                            {' / '}
                                            <Typography component="span" sx={{ fontSize: '0.66rem', color: '#10b981', fontWeight: 600 }}>{fmtPrice(plan.resistance)}</Typography>
                                          </Typography>
                                        </Box>
                                        <Box sx={{ minWidth: 55 }}>
                                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600, lineHeight: 1, mb: 0.2 }}>MA20</Typography>
                                          <Typography sx={{ fontSize: '0.66rem', fontWeight: 600, color: '#3b82f6' }}>{fmtPrice(plan.ma20)}</Typography>
                                        </Box>
                                        <Box sx={{ minWidth: 55, borderLeft: '1px solid #e2e8f0', pl: 1.5 }}>
                                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600, lineHeight: 1, mb: 0.2 }}>T+3</Typography>
                                          <Typography sx={{ fontSize: '0.66rem', fontWeight: 600, color: '#8b5cf6' }}>{fmtDate(getTplusDate())}</Typography>
                                        </Box>
                                      </Box>
                                    )}

                                    {/* Score History Chart */}
                                    {scoreHistoryLoading && expandedTicker === s.ticker && (
                                      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0' }}>
                                        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 1.5 }} animation="wave" />
                                      </Box>
                                    )}
                                    {!scoreHistoryLoading && scoreHistory?.ticker === s.ticker && scoreHistory?.history?.length > 0 && (
                                      <Box sx={{ px: 2, py: 1, borderTop: '1px solid #e2e8f0' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                          <Timeline sx={{ fontSize: 13, color: '#667eea' }} />
                                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569' }}>
                                            {t('riskOpportunityMatrix.scoreHistory.title', 'Score History')} ({scoreHistory.history.length}d)
                                          </Typography>
                                          {/* Quadrant change badges */}
                                          {(() => {
                                            const hist = scoreHistory.history;
                                            const quadrants = hist.map(h => h.quadrant);
                                            const changes = [];
                                            for (let i = 1; i < quadrants.length; i++) {
                                              if (quadrants[i] !== quadrants[i-1]) {
                                                changes.push({ from: quadrants[i-1], to: quadrants[i], date: hist[i].date });
                                              }
                                            }
                                            if (changes.length === 0) return null;
                                            const latest = changes[changes.length - 1];
                                            const isUp = _isUpgrade(latest.from, latest.to);
                                            return (
                                              <Chip
                                                size="small"
                                                icon={<SwapHoriz sx={{ fontSize: 11 }} />}
                                                label={`${latest.from}→${latest.to}`}
                                                sx={{
                                                  height: 18, fontSize: '0.58rem', fontWeight: 700, ml: 0.5,
                                                  bgcolor: isUp ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1),
                                                  color: isUp ? '#10b981' : '#ef4444',
                                                  '& .MuiChip-icon': { color: isUp ? '#10b981' : '#ef4444' },
                                                }}
                                              />
                                            );
                                          })()}
                                        </Box>
                                        <ResponsiveContainer width="100%" height={130}>
                                          <LineChart data={scoreHistory.history} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} tickFormatter={v => v.slice(5)} interval="preserveStartEnd" />
                                            <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: '#94a3b8' }} width={28} />
                                            <Tooltip
                                              contentStyle={{ fontSize: '0.68rem', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                                              labelFormatter={v => `${v}`}
                                              formatter={(v, name) => [v?.toFixed(1), name]}
                                            />
                                            <Line type="monotone" dataKey="opportunity" stroke="#22c55e" strokeWidth={2} dot={false} name="Opportunity" />
                                            <Line type="monotone" dataKey="risk" stroke="#ef4444" strokeWidth={2} dot={false} name="Risk" />
                                            <Line type="monotone" dataKey="priority" stroke="#667eea" strokeWidth={1.5} dot={false} name="Priority" strokeDasharray="4 2" />
                                            <ReferenceLine y={55} stroke="#94a3b8" strokeDasharray="3 3" strokeWidth={0.5} />
                                            <ReferenceLine y={45} stroke="#94a3b8" strokeDasharray="3 3" strokeWidth={0.5} />
                                            <Legend wrapperStyle={{ fontSize: '0.6rem' }} iconSize={8} />
                                          </LineChart>
                                        </ResponsiveContainer>
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })()}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                        </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>); })()}

              {/* Sector Analysis & Correlation Check */}
              {(() => {
                const stocks = quadrantData[selQ] || [];
                if (stocks.length === 0) return null;
                
                // Sector distribution
                const sectorMap = {};
                stocks.forEach(s => {
                  const sn = s.sector_name?.[i18n.language] || s.sector_code?.replace(/_/g, ' ') || 'Other';
                  if (!sectorMap[sn]) sectorMap[sn] = { count: 0, tickers: [], avgPriority: 0 };
                  sectorMap[sn].count++;
                  sectorMap[sn].tickers.push(s.ticker);
                  sectorMap[sn].avgPriority += (s.priority || 0);
                });
                Object.values(sectorMap).forEach(s => { s.avgPriority = s.avgPriority / s.count; });
                const sectors = Object.entries(sectorMap).sort((a, b) => b[1].count - a[1].count);
                const topSector = sectors[0];
                const concentration = topSector ? (topSector[1].count / stocks.length * 100) : 0;
                const isConcentrated = concentration > 50 && stocks.length >= 3;
                
                return (
                  <Box sx={{ px: 2, py: 1, borderTop: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600, mr: 0.5 }}>
                        {t('riskOpportunityMatrix.sectorAnalysis.sectors', 'Sectors')}:
                      </Typography>
                      {sectors.slice(0, 5).map(([name, data]) => (
                        <Box key={name} sx={{
                          display: 'flex', alignItems: 'center', gap: 0.3,
                          px: 0.6, py: 0.15, borderRadius: 0.5,
                          bgcolor: data.count === topSector[1].count ? alpha(Q_CFG[selQ].color, 0.1) : '#f1f5f9',
                        }}>
                          <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#475569' }}>{name}</Typography>
                          <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: Q_CFG[selQ].color }}>{data.count}</Typography>
                        </Box>
                      ))}
                      {isConcentrated && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, ml: 1, px: 0.6, py: 0.15, borderRadius: 0.5, bgcolor: 'rgba(245,158,11,0.1)' }}>
                          <Warning sx={{ fontSize: 10, color: '#f59e0b' }} />
                          <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#f59e0b' }}>
                            {t('riskOpportunityMatrix.sectorAnalysis.concentrated', 'Concentrated')}: {concentration.toFixed(0)}% {topSector[0]}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          </Box>
        </Box>
      )}

      {/* Tab 1: Safe Opportunities */}
      {tab === 1 && (
        <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 28, height: 28, borderRadius: 1.5, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Shield sx={{ fontSize: 16, color: '#fff' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                {t('riskOpportunityMatrix.safeEntry.title', 'Safe Entry Opportunities')}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                {safeOpportunities.length} {t('riskOpportunityMatrix.stocks', 'stocks')} · {t('riskOpportunityMatrix.safeEntry.subtitle', 'Strong trend, good entry zone, low risk')}
              </Typography>
            </Box>
          </Box>

          {safeOpportunities.length === 0 ? (
            <EmptyState msg={t('riskOpportunityMatrix.noSafeStocks', 'No safe opportunities found')} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { label: t('riskOpportunityMatrix.table.ticker', 'Ticker'), align: 'left' },
                      { label: t('riskOpportunityMatrix.table.sector', 'Sector'), align: 'left' },
                      { label: t('riskOpportunityMatrix.table.priority', 'Priority'), align: 'center' },
                      { label: 'Trend+RS', align: 'center' },
                      { label: 'Entry Setup', align: 'center' },
                      { label: '% MA20', align: 'center' },
                      { label: '% MA60', align: 'center' },
                      { label: '5d', align: 'center' },
                      { label: 'RSI', align: 'center' },
                      { label: t('riskOpportunityMatrix.table.status', 'Entry'), align: 'center' },
                    ].map((h, i) => (
                      <TableCell key={i} align={h.align}
                        sx={{ fontWeight: 700, fontSize: '0.65rem', color: '#64748b', py: 0.6, px: 0.8, borderColor: '#f1f5f9', ...(i === 0 && { pl: 2 }) }}>{h.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {safeOpportunities.map(s => {
                    // Entry quality assessment
                    const ma20 = s.price_vs_ma20;
                    const rsi = s.rsi_14;
                    const isIdeal = ma20 != null && ma20 >= -1 && ma20 <= 3 && rsi != null && rsi < 60;
                    const isGood = ma20 != null && ma20 >= -2 && ma20 <= 5 && rsi != null && rsi < 70;
                    const entryLabel = isIdeal ? 'Ideal' : isGood ? 'Good' : 'OK';
                    const entryClr = isIdeal ? '#10b981' : isGood ? '#3b82f6' : '#f59e0b';

                    return (
                      <TableRow key={s.ticker} hover onClick={() => navigate(`/stocks/${s.ticker}`)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha('#10b981', 0.03) } }}>
                        <TableCell sx={{ py: 0.5, px: 0.8, pl: 2, borderColor: '#f8fafc' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>{s.ticker}</Typography>
                            {s.quadrant === 'B' && (
                              <Typography sx={{ fontSize: '0.52rem', fontWeight: 700, color: '#f97316', bgcolor: alpha('#f97316', 0.08), px: 0.3, py: 0.05, borderRadius: 0.3 }}>B</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 0.8, borderColor: '#f8fafc' }}>
                          <Typography sx={{ fontSize: '0.62rem', color: '#64748b' }}>{s.sector_name?.[i18n.language] || s.sector_code?.replace(/_/g, ' ')}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, px: 0.8, borderColor: '#f8fafc' }}>
                          <Typography sx={{ display: 'inline-block', px: 0.5, py: 0.1, borderRadius: 0.8, bgcolor: alpha('#6366f1', 0.08), color: '#6366f1', fontWeight: 800, fontSize: '0.72rem' }}>
                            {s.priority?.toFixed(1) ?? '–'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 0.8, borderColor: '#f8fafc' }}>
                          <ScoreBar value={s.o1_trend} />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 0.8, borderColor: '#f8fafc' }}>
                          <ScoreBar value={s.o2_flow} />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, px: 0.8, borderColor: '#f8fafc' }}>
                          {s.price_vs_ma20 != null ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.2 }}>
                              {s.price_vs_ma20 >= 0 ? <TrendingUp sx={{ fontSize: 11, color: '#10b981' }} /> : <TrendingDown sx={{ fontSize: 11, color: '#ef4444' }} />}
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: s.price_vs_ma20 >= 0 && s.price_vs_ma20 <= 5 ? '#10b981' : s.price_vs_ma20 > 8 ? '#ef4444' : '#f59e0b' }}>
                                {s.price_vs_ma20.toFixed(1)}%
                              </Typography>
                            </Box>
                          ) : '–'}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, px: 0.8, borderColor: '#f8fafc' }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: (s.price_vs_ma60 || 0) > 15 ? '#ef4444' : '#64748b' }}>
                            {s.price_vs_ma60 != null ? `${s.price_vs_ma60.toFixed(1)}%` : '–'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, px: 0.8, borderColor: '#f8fafc' }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: (s.gain_5d || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                            {fmtPct(s.gain_5d)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, px: 0.8, borderColor: '#f8fafc' }}>
                          <Typography sx={{ fontSize: '0.68rem', color: (s.rsi_14 || 50) > 70 ? '#ef4444' : (s.rsi_14 || 50) < 30 ? '#10b981' : '#475569' }}>
                            {s.rsi_14?.toFixed(0) ?? '–'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, px: 0.8, borderColor: '#f8fafc' }}>
                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: entryClr, bgcolor: alpha(entryClr, 0.08), px: 0.5, py: 0.15, borderRadius: 0.5, display: 'inline-block' }}>
                            {entryLabel}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default RiskOpportunityMatrix;
